/**
 * React Query hooks for profile data
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useApiMutation } from '@/hooks/react-query/useApiMutation';
import { APIError } from '@/utils/api-client';
import {
  ContactUpdateRequest,
  ProfileData,
  ProfileService,
} from '../services/profile-service';

/**
 * Query key for profile data
 */
const profileKeys = {
  all: ['portal', 'profile'] as const,
  detail: () => [...profileKeys.all, 'detail'] as const,
};

/**
 * Hook to fetch current user's profile
 */
export function useProfile() {
  return useQuery({
    queryKey: profileKeys.detail(),
    queryFn: () => ProfileService.getProfile(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    // Always refetch on mount so approved change requests (applied in the
    // separate admin app) are reflected when the profile page is reopened,
    // without requiring a manual browser refresh.
    refetchOnMount: 'always',
  });
}

/**
 * Hook to update current user's profile
 */
export function useUpdateProfile() {
  const queryClient = useQueryClient();

  return useApiMutation<ProfileData, APIError, ContactUpdateRequest>(
    (data) => ProfileService.updateProfile(data),
    {
      mutationKey: ['portal', 'profile', 'update'],
      successMessage:
        'Your notification preferences have been saved successfully.',
      onSuccess: (updatedProfile) => {
        // Update the cache with the new profile data
        queryClient.setQueryData(profileKeys.detail(), updatedProfile);
      },
    }
  );
}
